/**
 * Página Módulos - Pantalla dedicada a Calculadora de presupuesto y Checklist por guía
 * Mismo diseño que el Dashboard (Topbar + Sidebar + contenido)
 * Ruta: /modulos
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useFirebase } from "../context/ApiContext";
import { DashboardLayout, ModulesSection } from "../components/Dashboard";
import Auth from "../components/Auth/Auth";
import { BasicModal } from "../components/BasicModal/BasicModal";

import "../components/Dashboard/Dashboard.css";

const ModulesPage = () => {
	const navigate = useNavigate();
	const { enqueueSnackbar } = useSnackbar();
	const {
		user,
		handleLogout: contextLogout,
		readProjects,
		createProject,
		createPage,
		getPassport,
		getTravelCard,
		canCreateGuide,
	} = useFirebase();

	const [showLogin, setShowLogin] = useState(false);
	const [authMode, setAuthMode] = useState("login");
	const [myGuides, setMyGuides] = useState([]);
	const [loadingGuides, setLoadingGuides] = useState(true);
	const [countriesCount, setCountriesCount] = useState(0);
	const [travelCard, setTravelCard] = useState(null);

	useEffect(() => {
		const loadGuides = async () => {
			if (!user) {
				setMyGuides([]);
				setLoadingGuides(false);
				return;
			}
			setLoadingGuides(true);
			try {
				const projects = await readProjects();
				setMyGuides(projects || []);
			} catch (error) {
				console.error("Error loading guides:", error);
				setMyGuides([]);
			} finally {
				setLoadingGuides(false);
			}
		};
		loadGuides();
	}, [user, readProjects]);

	useEffect(() => {
		const loadTravelerProfile = async () => {
			if (!user) {
				setCountriesCount(0);
				setTravelCard(null);
				return;
			}
			try {
				const passport = await getPassport();
				setCountriesCount(passport?.countries?.length || 0);
			} catch (_) {}
			try {
				const card = await getTravelCard();
				setTravelCard(card || null);
			} catch (_) {}
		};
		loadTravelerProfile();
	}, [user, getPassport, getTravelCard]);

	const formattedUser = user
		? {
				id: user.uid || user.id,
				displayName:
					user.displayName ||
					user.name ||
					user.email?.split("@")[0] ||
					"Usuario",
				email: user.email,
			}
		: null;

	const handleSectionChange = (sectionId) => {
		if (sectionId === "home") {
			navigate("/");
			return;
		}
		if (sectionId === "explore") {
			navigate("/explorar");
			return;
		}
		if (sectionId === "my-guides") {
			if (!user) {
				setAuthMode("login");
				setShowLogin(true);
				enqueueSnackbar("Inicia sesión para ver tus guías", {
					variant: "info",
				});
				return;
			}
			navigate("/mis-guias");
			return;
		}
		if (sectionId === "favorites") {
			if (!user) {
				setAuthMode("login");
				setShowLogin(true);
				enqueueSnackbar("Inicia sesión para ver tus favoritos", {
					variant: "info",
				});
				return;
			}
			navigate("/favoritos");
			return;
		}
		if (sectionId === "passport") {
			navigate("/passport");
			return;
		}
		if (sectionId === "travel-card") {
			navigate("/tarjeta-viajera");
			return;
		}
		if (sectionId === "modules") return; // ya estamos aquí
		if (sectionId === "discounts") {
			navigate("/descuentos");
			return;
		}
		if (sectionId === "create") {
			handleCreateFromScratch();
		}
	};

	const handleCreateFromScratch = async () => {
		if (user) {
			const createCheck = canCreateGuide();
			if (!createCheck.allowed) {
				enqueueSnackbar(
					`Has alcanzado el límite de ${createCheck.maxGuides} guías. Actualiza a Pro para guías ilimitadas.`,
					{ variant: "warning" }
				);
				navigate("/");
				return;
			}
		}
		navigate("/crear-guia");
	};

	const handleOpenMyGuide = async (guideId) => {
		if (!user) {
			setAuthMode("login");
			setShowLogin(true);
			return;
		}
		try {
			const project = myGuides.find((p) => p.id === guideId);
			if (project?.userId) {
				navigate(`/usuarios/${project.userId}/guias/${guideId}`);
			} else {
				const userId = user.uid || user.id;
				navigate(`/usuarios/${userId}/guias/${guideId}`);
			}
		} catch (error) {
			console.error("Error opening guide:", error);
			enqueueSnackbar("Error al abrir la guía", { variant: "error" });
		}
	};

	return (
		<>
			<DashboardLayout
				user={formattedUser}
				activeSection="modules"
				onSectionChange={handleSectionChange}
				onLogin={() => {
					setAuthMode("login");
					setShowLogin(true);
				}}
				onSignUp={() => {
					setAuthMode("signup");
					setShowLogin(true);
				}}
				onLogout={async () => {
					await contextLogout();
					enqueueSnackbar("Sesión cerrada", { variant: "success" });
				}}
				onCreateNew={handleCreateFromScratch}
				travelCard={travelCard}
				countriesCount={countriesCount}
			>
				<ModulesSection
					user={formattedUser}
					guides={myGuides}
					loading={loadingGuides}
					onLogin={() => {
						setAuthMode("login");
						setShowLogin(true);
					}}
					onCreateNew={handleCreateFromScratch}
					onOpenGuide={handleOpenMyGuide}
				/>
			</DashboardLayout>

			<BasicModal
				width={500}
				showModal={showLogin}
				handleClose={() => setShowLogin(false)}
			>
				<Auth
					setShowLogin={setShowLogin}
					setJustInitSession={() => {}}
					initialMode={authMode}
				/>
			</BasicModal>
		</>
	);
};

export default ModulesPage;
